import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useTranslationApp } from "../../lib/i18next";
import {
  TIKTOK_CREDIT_BANNER_CTA,
  TIKTOK_CREDIT_BANNER_DESCRIPTION_HIGHLIGHT,
  TIKTOK_CREDIT_BANNER_DESCRIPTION_PREFIX,
  TIKTOK_CREDIT_BANNER_EYEBROW,
  TIKTOK_CREDIT_BANNER_TITLE,
} from "../../i18n/keysTranslations";
import {
  GRADIENT_TIKTOK_CREDIT,
  GRADIENT_TIKTOK_CREDIT_COLOR_TEXT,
  TIKTOK_CREDIT_TEXT_SHADOW,
} from "../../utils/colors";
import { TIKTOK, TIKTOK_CREDIT_PERCENTAGE } from "../../utils/constants";
import { logoPlatform } from "../../utils/string";

// Sizes follow the banner's own width (container query), so it fits both the
// campaign modal and narrower places such as Settings on mobile.
const COMPACT = "@container (max-width: 480px)";
const NARROW = "@container (max-width: 340px)";

// Without onClick the banner is informational only (no call-to-action button).
// fullBleed drops the card chrome so a parent surface (e.g. a modal) can
// carry the gradient and the banner reads as that surface itself.
const TikTokCreditBanner = ({ nameBusiness, onClick, fullBleed = false }) => {
  const { t } = useTranslationApp();

  return (
    <Box
      data-testid="tiktok-credit-banner"
      sx={{
        position: "relative",
        overflow: "hidden",
        isolation: "isolate",
        containerType: "inline-size",
        mb: fullBleed ? 0 : 1.5,
        p: { xs: 1.75, sm: 2 },
        borderRadius: fullBleed ? 0 : "16px",
        background: fullBleed ? "transparent" : GRADIENT_TIKTOK_CREDIT,
        color: GRADIENT_TIKTOK_CREDIT_COLOR_TEXT,
        boxShadow: fullBleed
          ? "none"
          : "0px 10px 24px rgba(10, 10, 10, 0.22), inset 0 0 0 1px rgba(255, 255, 255, 0.06)",
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.625,
          py: 0.375,
          pl: 0.5,
          pr: 1,
          borderRadius: 999,
          backgroundColor: "rgba(255, 255, 255, 0.1)",
          border: "1px solid rgba(255, 255, 255, 0.14)",
        }}
      >
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            backgroundColor: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component="img"
            src={logoPlatform(TIKTOK)}
            alt=""
            sx={{ width: 12, height: 12 }}
          />
        </Box>
        <Typography
          component="span"
          sx={{
            fontSize: "0.6875rem",
            fontWeight: 700,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            color: "inherit",
          }}
        >
          {t(TIKTOK_CREDIT_BANNER_EYEBROW)}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mt: 1.25,
          mb: 1.5,
          [COMPACT]: { gap: 1.25 },
          [NARROW]: { gap: 1 },
        }}
      >
        <Typography
          component="span"
          sx={{
            flexShrink: 0,
            fontSize: "4.25rem",
            fontWeight: 900,
            letterSpacing: "-2px",
            lineHeight: 0.9,
            color: "inherit",
            textShadow: TIKTOK_CREDIT_TEXT_SHADOW,
            [COMPACT]: { fontSize: "3.5rem" },
            [NARROW]: { fontSize: "2.75rem", letterSpacing: "-1.5px" },
          }}
        >
          {TIKTOK_CREDIT_PERCENTAGE}
        </Typography>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "1.0625rem",
              fontWeight: 700,
              lineHeight: 1.25,
              color: "inherit",
              [COMPACT]: { fontSize: "0.95rem" },
              [NARROW]: { fontSize: "0.875rem" },
            }}
          >
            {t(TIKTOK_CREDIT_BANNER_TITLE)}
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              fontSize: "0.8125rem",
              lineHeight: 1.4,
              color: "rgba(255, 255, 255, 0.78)",
              [COMPACT]: { fontSize: "0.75rem" },
              [NARROW]: { fontSize: "0.7rem" },
            }}
          >
            {t(TIKTOK_CREDIT_BANNER_DESCRIPTION_PREFIX)}{" "}
            <Box component="strong" sx={{ color: "#fff", fontWeight: 700 }}>
              {t(TIKTOK_CREDIT_BANNER_DESCRIPTION_HIGHLIGHT, {
                NAME_BUSINESS: nameBusiness,
              })}
            </Box>
            .
          </Typography>
        </Box>
      </Box>
      {onClick ? (
        <ButtonBase
          onClick={onClick}
          sx={{
            width: "100%",
            minHeight: 46,
            px: 1.75,
            py: 0.75,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            borderRadius: 999,
            backgroundColor: "#fff",
            color: "#0A0A0A",
            fontFamily: "inherit",
            fontSize: "0.95rem",
            fontWeight: 800,
            textAlign: "center",
            boxShadow:
              "0 0 0 3px rgba(255, 255, 255, 0.12), 0 8px 18px rgba(0, 0, 0, 0.3)",
            transition: "transform 0.15s ease, box-shadow 0.15s ease",
            [COMPACT]: { minHeight: 42, fontSize: "0.875rem", gap: 0.75 },
            [NARROW]: { fontSize: "0.8rem", lineHeight: 1.25 },
            "&:hover": {
              transform: "translateY(-1px)",
              boxShadow:
                "0 0 0 4px rgba(255, 255, 255, 0.2), 0 12px 28px rgba(0, 0, 0, 0.4)",
            },
          }}
        >
          {t(TIKTOK_CREDIT_BANNER_CTA, { NAME_BUSINESS: nameBusiness })}
          <Box
            component="span"
            sx={{
              flexShrink: 0,
              width: 22,
              height: 22,
              borderRadius: "50%",
              backgroundColor: "#0A0A0A",
              color: "#fff",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* The theme forces SvgIcon color with !important. */}
            <ArrowForwardIcon sx={{ fontSize: 14, color: "#fff !important" }} />
          </Box>
        </ButtonBase>
      ) : null}
    </Box>
  );
};

export default TikTokCreditBanner;
